import {
  HbrIcon,
  HbrInput,
  HbrOption,
  HbrSelect
} from '@harbor/elements-react-standalone';
import { magnifyingGlass } from '@harbor/elements/icons';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useArchitectures } from '../../hooks/useToolboxHooks';
import { RootState } from '../../support/store';
import { Product } from '../../types/product';
import ProductCard from './ProductCard';
import NavHistoryTracker from '../../navigation/NavHistoryTracker';
import { asSingleValue } from '../../support/utils';

const ProductList = () => {
  const products: Product[] = useSelector((state: RootState) => state?.products?.ciscoOnly ?? []);
  const [search, setSearch] = useState('');
  const [selectedArchitectures, setSelectedArchitectures] = useState<number[]>([]);
  const { architectures, loading } = useArchitectures();

  const handleSearchChange = (value: string) => {
    setSearch(value.toLowerCase());
  };

  const filteredProducts = useMemo(() => {
    const allProducts = products ?? [];
    const selectedCount = selectedArchitectures.length;
    const filteredProducts = [];
    if (selectedCount > 0 && selectedCount < architectures.length + 1) {
      allProducts.forEach(product => {
        const productArchitectureIds = product.architecture.map(productArchitecture => productArchitecture.id);
        if (productArchitectureIds.some(paId => selectedArchitectures.includes(paId))) {
          filteredProducts.push(product);
        }
      });
    } else {
      filteredProducts.push(...allProducts);
    }
    if (search.length) {
      return (filteredProducts).filter(
        (product) =>
          (product?.name?.toLocaleLowerCase() ?? '').indexOf(search) > -1
      );
    } else {
      return filteredProducts;
    }
  }, [search, products, selectedArchitectures, architectures]);

  const usedArchitectures = useMemo(() => {
    return architectures.filter(architecture => {
      return products.some(product => (product?.architecture ?? []).map(arch => arch.id).includes(architecture.id))
    });
  }, [architectures, products]);

  const toggleArchitecture = (id) => {
    const selected = [...selectedArchitectures];
    const x = selected.indexOf(id);
    if (x === -1) {
      selected.push(id);
    } else {
      selected.splice(x, 1);
    }
    setSelectedArchitectures(selected);
  }

  return (
    <section className='mx-4 mt-4 mb-6'>
      <NavHistoryTracker title='All Products' />
      <header className='mb-4 mt-2 flex items-center gap-4'>
        <div className='flex'>
          <HbrInput
            placeholder='Search Products'
            value={search}
            clearable={true}
            onHbr-change={(e) => handleSearchChange(asSingleValue(e.target.value))}
            className=''
          >
            <HbrIcon
              slot='prefix'
              name='magnifying-glass'
              asset={magnifyingGlass}
              size='16px'
              staticColors={true}
            />
          </HbrInput>
        </div>
        <HbrSelect
          placeholder="All Architectures"
          variant="multiple"
          clearable={true}
          onHbr-clear={() => setSelectedArchitectures([])}
          data-testid="multi-external-anchor"
          className="hbr-css__form-section-md"
        >
          <span slot="counter" id="select-multi-counter-2">
            {selectedArchitectures.length}
          </span>
          <span slot="counter-label">{"Architectures"}</span>
          {usedArchitectures.map((architecture) => (
            <HbrOption key={architecture.id} value={architecture.id} onClick={() => toggleArchitecture(architecture.id)}>
              {architecture.name}
            </HbrOption>
          ))}
        </HbrSelect>
      </header>
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4'>
        {filteredProducts?.map((product) => (
          <ProductCard product={product} search={search} key={product.id} />
        ))}
      </div>
    </section>
  );
};

export default ProductList;
